import type { WarmupDurationSec } from '@vif/core';
import type { AppLocale } from '../i18n/index.js';
import type { ThemeName } from './theme.js';

/** App settings payload (lives inside the schema-versioned envelope, M1.2). */
export interface AppSettings {
  readonly theme: ThemeName;
  /** Index into READER_FONT_SIZES_PX (4 sizes, PRD §2.1). */
  readonly fontSize: 0 | 1 | 2 | 3;
  /** 'mono' (JetBrains Mono) is the gated experience; 'plex'/'code' are the
   * renamed IBM Plex Mono / Source Code Pro subsets; 'dyslexic' best-effort
   * (PRD §8.2). */
  readonly font: 'mono' | 'plex' | 'code' | 'dyslexic';
  readonly targetWpm: number;
  readonly adaptiveEnabled: boolean;
  readonly warmupEnabled: boolean;
  readonly warmupDurationSec: WarmupDurationSec;
  /** PD-17: adjustable warm-up start, 30–80, default 50. */
  readonly warmupStartPct: number;
  readonly orpColor: string | null; // null = theme accent
  readonly showGuides: boolean;
  readonly chunkSize: 1 | 2 | 3;
  readonly chunkLayout: 'vertical' | 'horizontal';
  readonly appLanguage: AppLocale;
  readonly onboardingComplete: boolean;
}

export const READER_FONT_SIZES_PX = [32, 44, 56, 68] as const;

export const DEFAULT_SETTINGS: AppSettings = {
  theme: 'dark',
  fontSize: 1,
  font: 'mono',
  targetWpm: 300,
  adaptiveEnabled: true,
  warmupEnabled: true,
  warmupDurationSec: 30,
  warmupStartPct: 50,
  orpColor: null,
  showGuides: true,
  chunkSize: 1,
  chunkLayout: 'vertical',
  appLanguage: 'en',
  onboardingComplete: false,
};

/**
 * Rehydrate settings from the stored envelope: defaults ← stored values ←
 * mapped legacy prototype values (only on keys the store has never written).
 * Every field is validated — a corrupt store never yields invalid settings.
 */
export function settingsFromStored(data: Record<string, unknown>): AppSettings {
  const legacy = (data['legacySettings'] ?? {}) as Record<string, unknown>;
  const pick = <T>(key: string, validate: (v: unknown) => T | undefined, fallback: T): T => {
    const stored = validate(data[key]);
    if (stored !== undefined) return stored;
    const mapped = validate(legacyMap(key, legacy));
    return mapped ?? fallback;
  };
  return {
    theme: pick('theme', oneOf(['dark', 'light', 'amoled'] as const), DEFAULT_SETTINGS.theme),
    fontSize: pick('fontSize', oneOf([0, 1, 2, 3] as const), DEFAULT_SETTINGS.fontSize),
    font: pick('font', oneOf(['mono', 'plex', 'code', 'dyslexic'] as const), DEFAULT_SETTINGS.font),
    targetWpm: pick('targetWpm', numberIn(100, 1200), DEFAULT_SETTINGS.targetWpm),
    adaptiveEnabled: pick('adaptiveEnabled', bool, DEFAULT_SETTINGS.adaptiveEnabled),
    warmupEnabled: pick('warmupEnabled', bool, DEFAULT_SETTINGS.warmupEnabled),
    warmupDurationSec: pick(
      'warmupDurationSec',
      oneOf([15, 30, 60] as const),
      DEFAULT_SETTINGS.warmupDurationSec,
    ),
    warmupStartPct: pick('warmupStartPct', numberIn(30, 80), DEFAULT_SETTINGS.warmupStartPct),
    orpColor: pick(
      'orpColor',
      (v) =>
        v === null || (typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v))
          ? (v as string | null)
          : undefined,
      DEFAULT_SETTINGS.orpColor,
    ),
    showGuides: pick('showGuides', bool, DEFAULT_SETTINGS.showGuides),
    chunkSize: pick('chunkSize', oneOf([1, 2, 3] as const), DEFAULT_SETTINGS.chunkSize),
    chunkLayout: pick(
      'chunkLayout',
      oneOf(['vertical', 'horizontal'] as const),
      DEFAULT_SETTINGS.chunkLayout,
    ),
    appLanguage: pick('appLanguage', oneOf(['en', 'tr'] as const), DEFAULT_SETTINGS.appLanguage),
    onboardingComplete: pick('onboardingComplete', bool, DEFAULT_SETTINGS.onboardingComplete),
  };
}

/** §6.6: prototype settings keys → new keys (values re-validated above). */
function legacyMap(key: string, legacy: Record<string, unknown>): unknown {
  switch (key) {
    case 'theme':
      return legacy['theme'];
    case 'targetWpm':
      return legacy['targetWpm'];
    case 'adaptiveEnabled':
      return legacy['adaptiveEnabled'];
    case 'warmupEnabled':
      return legacy['warmupEnabled'];
    case 'warmupDurationSec':
      return legacy['warmupDuration'];
    case 'orpColor':
      return legacy['orpColor'];
    case 'showGuides':
      return legacy['showGuides'];
    case 'chunkSize':
      return legacy['chunkSize'];
    case 'chunkLayout':
      return legacy['chunkLayout'];
    case 'onboardingComplete':
      return legacy['onboardingComplete'];
    default:
      return undefined;
  }
}

function oneOf<const T extends readonly unknown[]>(values: T) {
  return (v: unknown): T[number] | undefined => (values.includes(v) ? (v as T[number]) : undefined);
}

function bool(v: unknown): boolean | undefined {
  return typeof v === 'boolean' ? v : undefined;
}

function numberIn(min: number, max: number) {
  return (v: unknown): number | undefined =>
    typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max ? v : undefined;
}
